import { authorizeMe, axiosInstance } from "./commonHeadApiLogic";
import axios from "axios";

const UPLOAD_TIMEOUT_MS = 600000; // 10 minutes for large multipart uploads

const withAuthorization = async (apiFunction, ...args) => {
  try {
    await authorizeMe();
    return await apiFunction(...args);
  } catch (error) {
    console.error("Error in API request:", error);
    throw error;
  }
};

/**
 * Fetch editing plans.
 * If numberOfVideos is provided, fetches specific plans for that quantity.
 * If not provided, fetches all available plans summary.
 */
export async function getEditingPlans(numberOfVideos) {
  return withAuthorization(async () => {
    const url = numberOfVideos
      ? `/api/users/getplanBynumberOfVideos/${numberOfVideos}`
      : "/api/users/getplanBynumberOfVideos";
    const response = await axiosInstance.get(url);
    return response;
  });
}

export async function getAllEditingPlans() {
  return withAuthorization(async () => {
    const url = "/api/users/get-editing-plans";
    const response = await axiosInstance.get(url);
    return response;
  });
}

/**
 * Add an editing plan to the cart.
 * @param {string} editingPlanId - The ID of the editing plan.
 * @param {number} quantity - The quantity to add.
 */
export async function addToCart(editingPlanId, quantity = 1, fileKeys = []) {
  return withAuthorization(async () => {
    const url = "/api/users/cart/add";
    const payload = { editingPlanId, quantity, fileKeys };
    const response = await axiosInstance.post(url, payload);
    return response;
  });
}

/**
 * Fetch the user's current cart.
 */
export async function getMyCart() {
  return withAuthorization(async () => {
    const url = "/api/users/get-mycart";
    const response = await axiosInstance.get(url);
    return response;
  });
}

/**
 * Update the quantity of an item in the cart.
 * @param {string} editingPlanId - The ID of the editing plan.
 * @param {number} quantity - The quantity to adjust (usually 1).
 * @param {string} action - 'increase' or 'decrease'.
 */
export async function updateCartQuantity(itemId, quantity, action) {
  console.log("API CALL - updateCartQuantity:", { itemId, quantity, action });
  return withAuthorization(async () => {
    const url = "/api/users/cart/updateQuantity";
    const payload = { itemId, quantity, action };
    const response = await axiosInstance.post(url, payload);
    return response;
  });
}

/**
 * Start multipart upload
 */
export async function startUpload(fileName, fileType, veroaBookingId, fileSize) {
  return withAuthorization(async () => {
    const response = await axiosInstance.post(
      "/api/users/editing/upload/start",
      { fileName, fileType, veroaBookingId, fileSize },
      { timeout: UPLOAD_TIMEOUT_MS }
    );
    return response;
  });
}

/**
 * Get signed URL for a part
 */
export async function getPartUrl(uploadId, key, partNumber) {
  return withAuthorization(async () => {
    const response = await axiosInstance.post(
      "/api/users/editing/upload/get-part-url",
      { uploadId, key, partNumber },
      { timeout: UPLOAD_TIMEOUT_MS }
    );
    return response;
  });
}

/**
 * Upload bytes directly to DigitalOcean Spaces via presigned URL.
 * Multipart parts must NOT send Content-Type (not part of UploadPart signature).
 */
export async function uploadChunkToPresignedUrl(uploadUrl, chunk, fileType, onProgress, options = {}) {
  const { isMultipartPart = false } = options;
  const headers = isMultipartPart
    ? {}
    : { "Content-Type": fileType || "application/octet-stream" };

  const response = await axios.put(uploadUrl, chunk, {
    headers,
    timeout: UPLOAD_TIMEOUT_MS,
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      }
    },
  });
  return response;
}

/**
 * Upload a chunk through API (legacy fallback)
 */
export async function uploadChunk(chunk, uploadId, key, partNumber, onProgress) {
  const formData = new FormData();
  formData.append("chunk", chunk);
  formData.append("uploadId", uploadId);
  formData.append("key", key);
  formData.append("partNumber", partNumber);

  const response = await axiosInstance.post("/api/users/editing/upload/chunk", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: UPLOAD_TIMEOUT_MS,
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      }
    },
  });
  return response;
}

/**
 * Complete multipart upload (server reads parts from Spaces when parts omitted).
 */
export async function completeUpload(uploadId, key, parts, bookingid, veroaBookingId, category) {
  return withAuthorization(async () => {
    const response = await axiosInstance.post(
      "/api/users/editing/upload/complete",
      { uploadId, key, parts, bookingid, veroaBookingId, category },
      { timeout: UPLOAD_TIMEOUT_MS }
    );
    return response;
  });
}


/**
 * Abort multipart upload
 */
export async function abortUpload(uploadId, key) {
  return withAuthorization(async () => {
    const response = await axiosInstance.post("/api/users/editing/upload/abort", { uploadId, key });
    return response;
  });
}

/**
 * Update a booking with uploaded media keys
 */
export async function updateBookingMedia(bookingId, fileKeys) {
  return withAuthorization(async () => {
    const response = await axiosInstance.post("/api/users/editing/booking/update-media", { bookingId, fileKeys });
    return response;
  });
}

export async function deleteMedia(bookingId, key) {
  return withAuthorization(async () => {
    const response = await axiosInstance.post("/api/users/delete-media", { bookingId, key });
    return response;
  });
}

